import { SECTION_WIDGET_COMPONENT_IDS } from '../../appConsts/blocksIds';
import type { PopulatedSection } from '../../types/menusTypes';
import type { I$W, TFunction, Experiments } from '@wix/yoshi-flow-editor';
import { context } from '../../context/RootContext';
import type { SectionState } from '../../types/businessTypes';
import { SECTION_STATES } from '../../types/businessTypes';
import { navigationMenuState } from 'root/states/NavigationMenuState';
import { sectionsState } from 'root/states/SectionsState';
import { autorun } from 'mobx';
import { availabilityStatusKeys } from 'root/availabilityStatusKeys';
import { dispatchState } from 'root/states/DispatchState';
import { SPECS } from 'root/appConsts/experiments';
import { openDispatchModal } from 'root/utils/utils';

export class SectionsController {
  constructor(
    private $w: I$W,
    private t: TFunction,
    private isEditor: boolean,
    private timezone: string,
    private locale: string,
    private experiments: Experiments
  ) {
    context.pubsub.subscribe('onScrollToSection', this.scrollToSection);
  }

  setSections(sections: PopulatedSection[]) {
    this.$w(SECTION_WIDGET_COMPONENT_IDS.repeaterSections).data = sections.map(
      ({ id, ...rest }) => ({
        _id: id,
        ...rest,
      })
    );
  }

  init(sections: PopulatedSection[], menuId: string) {
    this.switchState(SECTION_STATES.sections);
    this.$w(SECTION_WIDGET_COMPONENT_IDS.repeaterSections).onItemReady(
      ($item: I$W, itemData: PopulatedSection & { _id: string }) => {
        const sectionImage = $item(SECTION_WIDGET_COMPONENT_IDS.sectionImage);
        if (itemData.image?.url) {
          sectionImage.expand();
          sectionImage.src = itemData.image.url;
        } else {
          sectionImage.collapse();
          this.isEditor && sectionImage.delete();
        }

        const title = $item(SECTION_WIDGET_COMPONENT_IDS.sectionTitle);
        title.text = itemData?.name || '';
        if (!itemData?.name) {
          title.collapse();
        } else {
          title.collapsed && title.expand();
        }

        const description = $item(SECTION_WIDGET_COMPONENT_IDS.sectionDescription);
        description.text = itemData?.description || '';
        if (!itemData?.description) {
          description.collapse();
        } else {
          description.collapsed && description.expand();
        }

        const sectionStatusElement = $item(SECTION_WIDGET_COMPONENT_IDS.sectionStatus);

        const enableSectionItemAvailability = this.experiments.enabled(
          SPECS.sectionItemAvailability
        );

        if (enableSectionItemAvailability) {
          const sectionState = sectionsState.getSection(itemData._id);

          const sectionAvailability = itemData.orderSettings?.availability;

          if (sectionAvailability) {
            sectionState?.updateSectionAvailability(sectionAvailability);
          }

          autorun(() => {
            sectionState?.initializeAvailability(
              dispatchState.dispatchInfo.selectedTimeSlot,
              dispatchState.timezone || this.timezone,
              dispatchState.currentOperation?.businessDaysAheadHandlingOptions
            );
          });

          const sectionStatusTextElement = $item(SECTION_WIDGET_COMPONENT_IDS.sectionStatusText);
          const sectionStatusButtonElement = $item(
            SECTION_WIDGET_COMPONENT_IDS.sectionStatusButton
          );

          autorun(() => {
            const isAvailable = !!sectionState?.isAvailable;
            const {
              shouldCollapseAvailabilityStatus,
              text = this.isEditor
                ? this.t('section.sectionAvailabilityStatus.noNextAvailability.text')
                : '',
            } =
              sectionState?.getAvailabilityStatus({
                locale: this.locale,
                timezone: dispatchState.timezone || this.timezone,
                t: this.t,
                keys: availabilityStatusKeys.section,
              }) ?? {};

            if (isAvailable || (!!shouldCollapseAvailabilityStatus && !this.isEditor)) {
              !sectionStatusElement.collapsed && sectionStatusElement.collapse();
            } else {
              sectionStatusElement.collapsed && sectionStatusElement.expand();

              sectionStatusTextElement.text = text;
              if (dispatchState.selectedDispatchType) {
                sectionStatusButtonElement.label = this.t(
                  `section.sectionAvailabilityStatus.nextAvailable.${dispatchState.selectedDispatchType.toLowerCase()}.button`
                );
              }

              sectionStatusButtonElement.onClick(() => {
                openDispatchModal({
                  onSave: async ({ dispatchType, dispatchInfo }) => {
                    dispatchState.update(dispatchType, dispatchInfo);
                    context.CartService?.setShippingDetails(dispatchState.getShippingDetails());
                    sectionsState.updateAvailabilityStatus(dispatchInfo);
                  },
                  context,
                  dispatchState: dispatchState.state,
                });
              });

              const hasNextAvailableTime = !!sectionState?.hasNextAvailableTimeslot;
              if (!hasNextAvailableTime && !this.isEditor) {
                !sectionStatusButtonElement.collapsed && sectionStatusButtonElement.collapse();
              } else {
                sectionStatusButtonElement.collapsed && sectionStatusButtonElement.expand();
              }
            }
          });
        } else {
          !sectionStatusElement.collapsed && sectionStatusElement.collapse();
        }

        const dishesContainer = $item(SECTION_WIDGET_COMPONENT_IDS.dishesContainer);
        dishesContainer.data = {
          itemIds: itemData?.itemIds,
          sectionId: itemData._id,
          menuId,
          size: itemData.size,
          isTruncated: itemData.truncated,
        };
        dishesContainer.onViewportEnter(() => {
          navigationMenuState.onSectionViewportEnter(itemData._id);
        });
        dishesContainer.onViewportLeave(() => {
          navigationMenuState.onSectionViewportLeave(itemData._id);
        });
      }
    );
    this.setSections(sections);
  }

  setEmptyState() {
    this.switchState(SECTION_STATES.sectionEmptystate);
    this.$w(SECTION_WIDGET_COMPONENT_IDS.sectionEmptyStateTitle).text = this.t(
      'menu_olo.emptyState.title'
    );
    this.$w(SECTION_WIDGET_COMPONENT_IDS.sectionEmptyStateSubtitle).text = this.t(
      'menu_olo.emptyState.subTitle'
    );
  }

  switchState(sectionState: SectionState) {
    const multiStateBox = this.$w(SECTION_WIDGET_COMPONENT_IDS.sectionMultiStateBox);
    multiStateBox.changeState(sectionState);
  }

  scrollToSection = ({ sectionId }: { sectionId: string }) => {
    navigationMenuState.setNavigating(true);
    setTimeout(
      () =>
        this.$w(SECTION_WIDGET_COMPONENT_IDS.repeaterSections).forItems(
          [sectionId],
          async ($item: I$W) => {
            await $item(SECTION_WIDGET_COMPONENT_IDS.sectionTitle).scrollTo();
            navigationMenuState.setNavigating(false);
          }
        ),
      0
    );
  };
}
